import { composeClassNames } from "@faedev/editor-utils";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { Card } from "react-bootstrap";
import Favorite from "src/components/Favorite";
import Property from "src/components/Property";
import Rating from "src/components/Rating";
import constants from "src/constants";
import getLink from "src/functions/getLink";
import translate from "src/functions/translate";
import { sLink } from "src/styles/sLink.module.scss";
import { sVideoCard } from "src/styles/sVideoCard.module.scss";
import { sVideoCardBody } from "src/styles/sVideoCardBody.module.scss";
import { sVideoCardFooter } from "src/styles/sVideoCardFooter.module.scss";
import { sVideoCardOverlay } from "src/styles/sVideoCardOverlay.module.scss";
import { ApiPlaylistCard } from "src/types/ApiPlaylistCard";
import { VariantSize } from "src/types/VariantSize";
import { VariantVideoPreview } from "src/types/VariantVideoPreview";

type Props = {
  className?: (string | string) | null | undefined;
  size: VariantSize;
  type: VariantVideoPreview;
  data?: ApiPlaylistCard;
  serialNo?: number | null | undefined;
  bunch?: number | null | undefined;
};

function PlaylistCard({ className, size, type, data, serialNo, bunch }: Props) {
  const { locale: lang } = useRouter();

  return data && data.thumbnail ? (
    <>
      <Card
        className={composeClassNames([
          sVideoCard,
          type,
          size,
          className,
          "video",
        ])}
        id={`video${data.id}`}
      >
        <div className="card-img-wrapper">
          <Image
            src={data.thumbnail?.url}
            alt={data.title}
            layout="fill"
            className="card-img"
          />
          <Card.ImgOverlay
            className={composeClassNames([
              sVideoCardOverlay,
              type,
              size,
              "video-overlay",
            ])}
          >
            <Rating
              value={data.rating}
              size={size}
              className={`video-rating`}
              colors={{
                default: constants.Theme.white,
                active: constants.Theme.white,
              }}
            />
            <Favorite
              setByUser={true}
              size={size}
              className={`video-favorite`}
              colors={{
                default: constants.Theme.white,
                active: constants.Theme.white,
              }}
              value={true}
            />
            <Link
              href={getLink(lang, "playlist", data.slug)}
              passHref
              locale={lang}
            >
              <Card.Link className={composeClassNames([sLink, `img-link`])} />
            </Link>
          </Card.ImgOverlay>
        </div>
        <Card.Body
          className={composeClassNames([sVideoCardBody, size, "video-body"])}
        >
          <Card.Title>
            <Link
              href={getLink(lang, "playlist", data.slug)}
              passHref
              locale={lang}
            >
              <Card.Link className={composeClassNames([sLink, `video-link`])}>
                {data.title}
              </Card.Link>
            </Link>
          </Card.Title>
          {(function () {
            switch (type) {
              case `preview`:
                return (
                  <Card.Text>
                    {data.practice_styles && (
                      <>
                        {data.practice_styles.slice(0, 3).map((item, index) => (
                          <>
                            {index > 0 && ` • `}
                            {translate(item.title.replace(/_/g, ``), lang)}
                          </>
                        ))}
                      </>
                    )}
                  </Card.Text>
                );
              case `serie`:
                return (
                  <Card.Text className={`video-label`}>{data.label}</Card.Text>
                );
              case `class`:
                return (
                  <Card.Text className={`video-label`}>{data.label}</Card.Text>
                );
            }
          })()}
        </Card.Body>
        {(function () {
          switch (type) {
            case `preview`:
              return data.difficulty ||
                data.practice_focuses ||
                data.views > 0 ? (
                <Card.Footer
                  className={composeClassNames([
                    sVideoCardFooter,
                    size,
                    "video-footer",
                  ])}
                >
                  {data.difficulty ? (
                    <Property
                      size={size}
                      className={`video-property`}
                      type={`difficulty`}
                      showIcon={true}
                      translation={true}
                      value={data.difficulty}
                    />
                  ) : (
                    <div></div>
                  )}
                  <Property
                    size={size}
                    className={`video-property`}
                    type={`focus`}
                    showIcon={true}
                    translation={true}
                    value={
                      data.practice_focuses && data.practice_focuses[0]?.title
                        ? data.practice_focuses[0].title
                        : null
                    }
                  />
                  <Property
                    size={size}
                    className={`video-property`}
                    type={`views`}
                    showIcon={true}
                    value={
                      data.views
                        ? String(Math.round(data.views / 1000)) + ` k`
                        : null
                    }
                  />
                </Card.Footer>
              ) : null;
            case `serie`:
              return null;
            case `class`:
              return null;
          }
        })()}
      </Card>
    </>
  ) : null;
}

export default PlaylistCard;
